import type { ExtendedTrackerViewT } from "@/types/database/trackers";
import { UUIDs } from "@/types/uuids";
import { Lightbulb } from "lucide-react";
import {
	Eq5dChart,
	FaamChart,
	GenericChart,
	HeelPainComplianceChart,
	OxfaqChart,
	OxfordKneeChart,
	PainChart,
	WOMACChart,
} from "./components";

interface TrackerChartsProps {
	disableAnimations?: boolean;
	tracker: ExtendedTrackerViewT;
}

const NoDataPlaceholder = () => (
	<div className="flex flex-col justify-center items-center p-8 text-muted-foreground text-center">
		<Lightbulb className="mb-2 w-8 h-8" />
		<h3 className="mb-1 font-semibold text-lg">No Data Yet</h3>
		<p className="text-sm">
			Add your first entry to start tracking your progress
		</p>
	</div>
);

export function TrackerChart({
	tracker,
	disableAnimations = false,
}: TrackerChartsProps) {
	const formId = tracker.form_id;
	const hasEntries = tracker.entries_view?.length > 0;

	if (!formId) {
		return null;
	}

	if (!hasEntries) {
		return <NoDataPlaceholder />;
	}

	return (
		<div className="my-2 w-full">
			{(() => {
				if (formId === UUIDs.forms.womac) {
					return <WOMACChart entries={tracker.entries_view} formId={formId} />;
				}

				if (formId === UUIDs.forms.oxfordKnee) {
					return (
						<OxfordKneeChart entries={tracker.entries_view} formId={formId} />
					);
				}

				if (formId === UUIDs.forms.eq5d5l) {
					return <Eq5dChart entries={tracker.entries_view} formId={formId} />;
				}

				if (formId === UUIDs.forms.moxfq) {
					return (
						<OxfaqChart
							disableAnimations={disableAnimations}
							entries={tracker.entries_view}
							formId={formId}
						/>
					);
				}

				if (formId === UUIDs.forms.faam) {
					return (
						<FaamChart
							disableAnimations={disableAnimations}
							entries={tracker.entries_view}
							formId={formId}
						/>
					);
				}

				if (formId === UUIDs.forms.heelpain) {
					return (
						<HeelPainComplianceChart
							disableAnimations={disableAnimations}
							entries={tracker.entries_view}
							formId={formId}
						/>
					);
				}

				if (
					Object.values(UUIDs.painScores).includes(
						formId as UUIDs["painScores"][keyof UUIDs["painScores"]],
					)
				) {
					return (
						<PainChart
							disableAnimations={disableAnimations}
							entries={tracker.entries_view}
							formId={formId}
						/>
					);
				}

				return (
					<GenericChart
						disableAnimations={disableAnimations}
						entries={tracker.entries_view}
						formId={formId}
					/>
				);
			})()}
		</div>
	);
}

export default TrackerChart;
